import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type HubspotImportApplyPayload,
  type HubspotImportApplyResponse,
} from './types';
import {
  zodHubspotImportApplyEndpointSchema,
  zodHubspotImportApplyPayloadSchema,
  zodHubspotImportApplyResponseSchema,
} from './validations';

/**
 * Marks the assigned supplier companies as relevant for a buyer company, using
 * the assignments an admin confirmed after a HubSpot import.
 *
 * @param companyId - The UUID of the buyer company the suppliers belong to.
 * @param assignments - Confirmed pairs of input name and supplier company id.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin---Companies/operation/post_hubspot_import_apply_vadmin_company__company_id__hubspot_import_apply_post
 */
export const hubspotImportApply = async (
  companyId: UUID,
  assignments: HubspotImportApplyPayload,
) => {
  try {
    return await vestigasApi
      .post(ENDPOINT.COMPANY.HUBSPOT_IMPORT.APPLY(companyId), {
        json: assignments,
        validate: {
          body: zodHubspotImportApplyPayloadSchema,
          pathSegments: zodHubspotImportApplyEndpointSchema,
          response: zodHubspotImportApplyResponseSchema,
        },
      })
      .json<HubspotImportApplyResponse>();
  } catch (error) {
    log.error({
      message: 'Error applying HubSpot supplier import',
      error,
    });

    let errorMessage: string = i18n.t(
      ($) => $.hubspotImport.apply.error.generic,
      {
        ns: 'data',
      },
    );

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.hubspotImport.apply.error422, {
            ns: 'data',
          });
          break;
        }
      }
    }

    ToastService.error(errorMessage);

    throw error;
  }
};
